import { Alert, message, Spin, Tag, Space, Modal } from "antd";
import React, { useEffect } from "react";
import { Button, List, Icon, Segment } from "semantic-ui-react";
import useSWR from "swr";
import EmailRecord from "../../../../../types/admin/EmailRecord";
import { RequestError } from "../../../../../types/Error";
import { fetcher } from "../../../../../util/request";
import axios, { AxiosError } from "axios";

interface EmailHistoryProps {
  applicationId: string;
  goBack: () => void;
}

const EmailHistory: React.FC<EmailHistoryProps> = ({
  applicationId,
  goBack,
}) => {
  const {
    data: emailRecords,
    error,
    isLoading,
    mutate: refreshEmailRecords,
  } = useSWR<EmailRecord[], RequestError>(
    `/admin/email-record/list/${applicationId}`,
    fetcher,
  );

  useEffect(() => {
    if (error) {
      message.error("Failed to fetch email records: " + error.message);
      goBack();
    }
  }, [error, goBack]);

  if (isLoading) return <Spin />;
  if (!emailRecords) return null;

  return (
    <div className="email-history">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3>Email History</h3>
        <Button
          color="grey"
          onClick={goBack}
          icon="arrow left"
          content="Back"
        />
      </div>
      {emailRecords.length > 0 ? (
        <EmailRecordList
          records={emailRecords}
          refreshEmailRecords={refreshEmailRecords}
        />
      ) : (
        <Alert message="No email records found" type="info" />
      )}
    </div>
  );
};

const EmailRecordList: React.FC<{
  records: EmailRecord[];
  refreshEmailRecords: () => void;
}> = ({ records, refreshEmailRecords }) => (
  <List divided relaxed>
    {records.map((record) => (
      <EmailRecordItem
        key={record.id}
        record={record}
        refreshEmailRecords={refreshEmailRecords}
      />
    ))}
  </List>
);

const EmailRecordItem: React.FC<{
  record: EmailRecord;
  refreshEmailRecords: () => void;
}> = ({ record, refreshEmailRecords }) => {
  const scheduledTime = new Date(record.scheduledTimeISO).toLocaleString();
  const emailParams = Object.entries(record.emailParams).map(([key, value]) => (
    <React.Fragment key={key}>
      <b>{key}</b>:{" "}
      {value ? value : <span style={{ color: "gray" }}>(empty)</span>} <br />
    </React.Fragment>
  ));
  const getEmailStatusColor = (status: string): string => {
    switch (status) {
      case "SENT":
        return "green";
      case "SCHEDULED":
        return "blue";
      case "CANCELLED":
        return "yellow";
      default:
        return "red";
    }
  };
  const cancelEmail = () => {
    Modal.confirm({
      title: "Are you sure you want to cancel this email?",
      content: "This action cannot be undone.",
      okText: "Confirm",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          const res = await axios.patch(
            `/admin/email-record/cancel/${record.id}`,
          );
          message.success(res.data.message);
        } catch (err) {
          if (err instanceof AxiosError) {
            message.error(err?.response?.data?.message);
          } else {
            message.error("Failed to cancel this email.");
          }
        } finally {
          refreshEmailRecords();
        }
      },
    });
  };
  return (
    <List.Item>
      <List.Icon name="mail" size="large" verticalAlign="top" />
      <List.Content>
        <List.Header className="d-flex gap-2 align-items-center">
          {record.emailId}
          {record.emailStatus && (
            <Tag
              color={getEmailStatusColor(record.emailStatus)}
              style={{ padding: "4px 8px" }}
            >
              {record.emailStatus}
            </Tag>
          )}
          {record.emailStatus === "SCHEDULED" && (
            <Button
              size="mini"
              color="red"
              content="Cancel"
              icon="trash alternate"
              onClick={cancelEmail}
            />
          )}
        </List.Header>
        <List.Description>
          {scheduledTime} <br />
          {emailParams.length > 0 && (
            <>
              <span className="text-muted">Additional parameters:</span>
              <br />
              {emailParams}
            </>
          )}
        </List.Description>

        {/* attachments Section */}
        {record.attachmentFilenames &&
          record.attachmentFilenames.length > 0 && (
            <Segment size="small" secondary className="my-2">
              <div className="mb-1">
                <Icon name="paperclip" />
                <span className="fw-bold">Attachments:</span>
              </div>
              <Space wrap size="small">
                {record.attachmentFilenames.map((filename, i) => (
                  <Tag key={i} color="blue" style={{ padding: "4px 8px" }}>
                    <Icon name="file" />
                    {filename}
                  </Tag>
                ))}
              </Space>
            </Segment>
          )}
      </List.Content>
    </List.Item>
  );
};

export default EmailHistory;
